import * as React from 'react';
import { Box, Button, Paper, Popper, Grow, Stack, MenuItem, MenuList, ClickAwayListener } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InvoiceStatusEnum } from 'src/shared/models/enums/invoiceStatusEnum';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { CancelInvoiceDialog } from './cancelInvoiceDialog';
import { PaymentTransactionCreateModal } from './paymentTransactionCreateModal';
import { Invoice } from '../models/invoice';

type HandleInvoiceMenuProps = {
  invoice: Invoice;
  disabled: boolean;
  refetchData?: () => void;
};

const HandleInvoiceMenu = (props: HandleInvoiceMenuProps) => {
  const { invoice, disabled, refetchData } = props;
  const { t } = useTranslation(['invoice', 'common']);
  const { setDateStamp } = useGlobals();

  const [showCancelInvoiceDialog, setShowCancelInvoiceDialog] = React.useState(false);
  const [showPaymentTransactionCreateModal, setShowPaymentTransactionCreateModal] = React.useState<boolean>(false);
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleCancelInvoice = () => {
    setOpen(false);
    setShowCancelInvoiceDialog(true);
  };

  const handleCancelInvoiceCallback = () => {
    setShowCancelInvoiceDialog(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <CancelInvoiceDialog invoiceId={invoice.invoice_id} showDialog={showCancelInvoiceDialog} callback={handleCancelInvoiceCallback} />
      {showPaymentTransactionCreateModal && (
        <PaymentTransactionCreateModal
          onClose={() => setShowPaymentTransactionCreateModal(false)}
          onSave={(id?: number) => {
            ConsoleHelper.log('InvoiceId: ', id);
            setShowPaymentTransactionCreateModal(false);
            setDateStamp(new Date());
            // Om-hämta fakturan så nyregistrerad betalning + uppdaterad betalstatus/saldo syns direkt (utan sidladdning).
            refetchData?.();
          }}
          initialValues={{
            ocr: invoice.ocr,
            amount: invoice.amount,
            comment: '',
            date: null,
            source: '',
            sender: '',
          }}
          modalOpen={showPaymentTransactionCreateModal}
        />
      )}
      <Stack direction='row' spacing={2} sx={{ ml: 2 }}>
        <Box>
          <Button
            ref={anchorRef}
            id='composition-button'
            aria-controls={open ? 'composition-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup='true'
            onClick={handleToggle}
            variant='contained'
            disabled={disabled}
            disableElevation
            endIcon={<ArrowDropDown />}
          >
            {t('common:button.handle')}
          </Button>
          <Popper open={open} anchorEl={anchorRef.current} placement='bottom-start' transition disablePortal nonce={undefined} sx={{ zIndex: 999 }}>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleClose}>
                    <MenuList id='composition-menu' aria-labelledby='composition-button'>
                      <MenuItem onClick={handleCancelInvoice} disabled={invoice.status.id === InvoiceStatusEnum.Canceled}>
                        {t('invoice:Cancel')}
                      </MenuItem>
                      {/* <MenuItem onClick={handleClose} disabled>
                      {t('invoice:RegisterPayment')}
                    </MenuItem> */}
                      <MenuItem
                        onClick={() => setShowPaymentTransactionCreateModal(true)}
                        disabled={invoice.status.id === InvoiceStatusEnum.Canceled}
                      >
                        {t('invoice:RegisterPayment')}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Stack>
    </>
  );
};

export default HandleInvoiceMenu;
